import React, { useState, useEffect, useCallback } from 'react';
import { setKillSwitch, getRiskStatus, updateRiskConfig } from '../../api/client';

function GaugeBar({ value, max, warnPct = 70, dangerPct = 90, label, sublabel }) {
  const pct     = Math.min(100, max > 0 ? (value / max) * 100 : 0);
  const color   = pct >= dangerPct ? 'var(--red)' : pct >= warnPct ? 'var(--yellow)' : 'var(--green)';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
        <span style={{ color: 'var(--muted)' }}>{label}</span>
        <span style={{ color, fontWeight: 600 }}>{sublabel}</span>
      </div>
      <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className="stat-card" style={{ padding: '10px 14px' }}>
      <div className="stat-label" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 20, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

const MARGIN_STATUS_META = {
  ok:      { color: 'var(--green)', label: 'Healthy' },
  warn:    { color: 'var(--yellow)', label: 'Elevated' },
  halt:    { color: 'var(--red)',   label: 'Critical — halted' },
  unknown: { color: 'var(--muted)', label: 'Unavailable' },
};

export default function RiskPanel() {
  const [status,  setStatus]  = useState(null);
  const [config,  setConfig]  = useState(null);   // local editable copy
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getRiskStatus();
      setStatus(res.data);
      // Initialise config form only on first load
      setConfig(prev => prev ?? res.data.config);
    } catch (e) {
      setError('Could not load risk status.');
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 10000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateRiskConfig(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await fetchStatus();
    } catch {
      setError('Failed to save config.');
    } finally {
      setSaving(false);
    }
  };

  const handleKillSwitch = async (active) => {
    try { await setKillSwitch(active); await fetchStatus(); } catch {}
  };

  if (!status || !config) {
    return (
      <div>
        <h1 className="page-title">🛡️ Risk Management</h1>
        <div style={{ color: 'var(--muted)', padding: 24 }}>Loading risk data…</div>
      </div>
    );
  }

  const { margin, daily_pnl, trade_count, kill_switch } = status;
  const cfg = status.config;

  return (
    <div>
      <h1 className="page-title">🛡️ Risk Management</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* ── Left column: config ─────────────────────────────────────────── */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>⚙️ Risk Limits</div>

            {/* P&L limits */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              P&amp;L Controls
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>Max Daily Loss (₹)</label>
              <input className="input" type="number" min={0}
                value={config.max_daily_loss}
                onChange={e => setConfig(c => ({ ...c, max_daily_loss: Number(e.target.value) }))} />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Kill switch fires when daily realized P&amp;L hits this loss.
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '16px 0' }} />

            {/* Trade frequency */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Trade Frequency
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>
                Max Trades Per Day
                <span style={{ marginLeft: 6, color: 'var(--accent)', fontSize: 11 }}>
                  (current: {trade_count} / {cfg.max_trades_per_day})
                </span>
              </label>
              <input className="input" type="number" min={1} max={100}
                value={config.max_trades_per_day}
                onChange={e => setConfig(c => ({ ...c, max_trades_per_day: Number(e.target.value) }))} />
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '16px 0' }} />

            {/* Session time controls */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Session Controls
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>
                No New Trades After
                <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent)' }}>
                  (existing positions continue running)
                </span>
              </label>
              <input
                className="input" type="time"
                value={config.no_trade_after_time || ''}
                onChange={e => setConfig(c => ({ ...c, no_trade_after_time: e.target.value }))}
              />
              {config.no_trade_after_time && (
                <button
                  onClick={() => setConfig(c => ({ ...c, no_trade_after_time: '' }))}
                  style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>
                Auto-Exit All Positions At
                <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--red)' }}>
                  (closes all open positions once)
                </span>
              </label>
              <input
                className="input" type="time"
                value={config.auto_exit_time || ''}
                onChange={e => setConfig(c => ({ ...c, auto_exit_time: e.target.value }))}
              />
              {config.auto_exit_time && (
                <button
                  onClick={() => setConfig(c => ({ ...c, auto_exit_time: '' }))}
                  style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Clear
                </button>
              )}
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '16px 0' }} />

            {/* Margin thresholds */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Margin Utilisation Thresholds
            </div>
            {[
              { key: 'margin_alert_pct', label: 'Warn at (%)' },
              { key: 'margin_halt_pct',  label: 'Halt at (%)' },
            ].map(({ key, label }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
                <input className="input" type="number" min={50} max={100} step={1}
                  value={config[key]}
                  onChange={e => setConfig(c => ({ ...c, [key]: Number(e.target.value) }))} />
              </div>
            ))}

            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save All Limits'}
            </button>
          </div>
        </div>

        {/* ── Right column: live gauges ────────────────────────────────────── */}
        <div>

          {/* Kill switch */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>🚨 Kill Switch</div>
            <GaugeBar
              value={Math.max(0, -(daily_pnl ?? 0))} max={cfg.max_daily_loss}
              warnPct={60} dangerPct={90}
              label="Daily Loss Used"
              sublabel={`₹${Math.max(0, -(daily_pnl ?? 0)).toFixed(0)} / ₹${cfg.max_daily_loss.toLocaleString('en-IN')}`}
            />
            <button
              className={`kill-switch-btn ${kill_switch ? 'active' : 'inactive'}`}
              style={{ width: '100%' }}
              onClick={() => handleKillSwitch(!kill_switch)}
            >
              {kill_switch ? '🛑 KILL SWITCH ON — Click to Reset' : '⬛ Kill Switch Armed (Off)'}
            </button>
          </div>

          {/* Trades / day */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>📊 Trade Frequency</div>
            <GaugeBar
              value={trade_count} max={cfg.max_trades_per_day}
              warnPct={70} dangerPct={90}
              label="Trades Today"
              sublabel={`${trade_count} / ${cfg.max_trades_per_day}`}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <StatBadge label="Trades Today" value={trade_count}
                color={trade_count >= cfg.max_trades_per_day ? 'var(--red)' : 'var(--text)'} />
              <StatBadge label="Remaining" value={Math.max(0, cfg.max_trades_per_day - trade_count)}
                color={cfg.max_trades_per_day - trade_count <= 2 ? 'var(--yellow)' : 'var(--green)'} />
            </div>
          </div>

          {/* Margin */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>💰 Margin Utilisation</div>
              {margin.status !== 'unknown' && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  color: MARGIN_STATUS_META[margin.status]?.color,
                  background: MARGIN_STATUS_META[margin.status]?.color + '18',
                  border: `1px solid ${MARGIN_STATUS_META[margin.status]?.color}44`,
                }}>
                  {MARGIN_STATUS_META[margin.status]?.label}
                </span>
              )}
            </div>
            {margin.status === 'unknown' ? (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Connect Fyers to see live margin data.
              </div>
            ) : (
              <>
                <GaugeBar
                  value={margin.utilisation_pct} max={100}
                  warnPct={cfg.margin_alert_pct} dangerPct={cfg.margin_halt_pct}
                  label="Used Margin"
                  sublabel={`${margin.utilisation_pct}%`}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <StatBadge label="Used (₹)" value={`₹${(margin.used / 1000).toFixed(1)}K`} />
                  <StatBadge label="Available (₹)" value={`₹${(margin.available / 1000).toFixed(1)}K`} />
                  <StatBadge label="Total (₹)" value={`₹${(margin.total / 1000).toFixed(1)}K`} />
                </div>
              </>
            )}
          </div>

          {/* Session time controls status */}
          {(cfg.auto_exit_time || cfg.no_trade_after_time) && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>⏰ Session Controls</div>
              {cfg.no_trade_after_time && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>No new trades after</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                    background: 'rgba(99,102,241,0.15)', color: 'var(--accent)',
                    border: '1px solid rgba(99,102,241,0.3)',
                  }}>
                    {cfg.no_trade_after_time}
                  </span>
                </div>
              )}
              {cfg.auto_exit_time && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Auto-exit all at</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                    background: 'rgba(248,113,113,0.15)', color: 'var(--red)',
                    border: '1px solid rgba(248,113,113,0.3)',
                  }}>
                    {cfg.auto_exit_time}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Kill switch explainer */}
          <div className="card">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>ℹ️ How the Kill Switch Works</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.8 }}>
              <div>🔴 <strong style={{ color: 'var(--text)' }}>Auto-fires</strong> the moment realized daily loss hits the limit — checked every second and on every trade close.</div>
              <div style={{ marginTop: 6 }}>🔒 <strong style={{ color: 'var(--text)' }}>Blocks all new entries</strong> while active. Open positions continue running normally.</div>
              <div style={{ marginTop: 6 }}>🔁 <strong style={{ color: 'var(--text)' }}>Reset manually</strong> via the button above — or resets automatically at 09:15 next trading day.</div>
              <div style={{ marginTop: 6 }}>📐 <strong style={{ color: 'var(--text)' }}>SL / Target / Trailing SL</strong> are configured per-strategy — see the Strategies page.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
